import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSocket } from './socket';
import { io } from 'socket.io-client';
import { SOCKET_URL } from '../socketConfig';

const LocalOpsLiveContext = createContext(null);
const MAX_LIVE_EVENTS = 100;
const LOCALOPS_LIVE_BATCH_MS = 180;
const LOCALOPS_LIVE_ENTITY_TYPES = new Set(['task', 'offering', 'share', 'join']);
const LOCALOPS_LIVE_EVENT_TYPES = new Set([
  'created',
  'accepted',
  'completed',
  'shared',
  'joined',
]);

const normalizeText = (value) => {
  const text = String(value || '').trim();
  return text.length ? text : '';
};

const normalizeVisibility = (value) => {
  const text = normalizeText(value).toLowerCase();
  if (text === 'auth' || text === 'public') return text;
  return '';
};

const buildSummaryText = ({ entityType, eventType, title }) => {
  const safeTitle =
    title ||
    (entityType === 'task'
      ? 'Task'
      : entityType === 'offering'
      ? 'Offering'
      : 'LocalOps');
  const entityLabel =
    entityType === 'offering'
      ? 'Offering'
      : entityType === 'task'
      ? 'Task'
      : 'LocalOps';
  if (eventType === 'created') {
    return `New ${entityLabel.toLowerCase()}: ${safeTitle}`;
  }
  if (eventType === 'accepted') {
    return `${entityLabel} accepted: ${safeTitle}`;
  }
  if (eventType === 'completed') {
    return `${entityLabel} completed: ${safeTitle}`;
  }
  if (eventType === 'shared') {
    return `${entityLabel} shared: ${safeTitle}`;
  }
  if (eventType === 'joined') {
    return `${entityLabel} joined: ${safeTitle}`;
  }
  return `LocalOps update: ${safeTitle}`;
};

const normalizeActor = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const playerId = normalizeText(payload.playerId);
  const profilePhoto = normalizeText(payload.profilePhoto);
  if (!playerId && !profilePhoto) return null;
  return {
    playerId: playerId || null,
    profilePhoto: profilePhoto || null,
  };
};

const normalizeBusiness = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const name = normalizeText(payload.name);
  const logo = normalizeText(payload.logo);
  if (!name && !logo) return null;
  return {
    name: name || null,
    logo: logo || null,
  };
};

const normalizeLivePayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const visibility = normalizeVisibility(payload.visibility);
  const entityType = normalizeText(payload.entityType).toLowerCase();
  const eventType = normalizeText(payload.eventType).toLowerCase();
  const shareUrl = normalizeText(payload.shareUrl);
  const title = normalizeText(payload.title);
  if (!visibility || !entityType || !eventType || !shareUrl || !title) {
    return null;
  }
  if (!LOCALOPS_LIVE_ENTITY_TYPES.has(entityType)) return null;
  if (!LOCALOPS_LIVE_EVENT_TYPES.has(eventType)) return null;

  const regionLabel = normalizeText(payload.regionLabel);
  const createdAtRaw = payload.createdAt;
  const createdAtParsed =
    typeof createdAtRaw === 'number'
      ? createdAtRaw
      : Date.parse(normalizeText(createdAtRaw));
  const createdAt = Number.isFinite(createdAtParsed)
    ? createdAtParsed
    : Date.now();

  const summaryText = buildSummaryText({
    entityType,
    eventType,
    title,
  });

  const actor = normalizeActor(payload.actor);
  const business = normalizeBusiness(payload.business);
  const baseType =
    entityType === 'task' || entityType === 'offering'
      ? entityType
      : 'localops';

  return {
    id: `${shareUrl}:${eventType}:${createdAt}`,
    type: `${baseType}.${eventType}`,
    createdAt,
    summaryText,
    regionLabel,
    visibility,
    title,
    shareUrl,
    actor,
    business,
  };
};

export function LocalOpsLiveProvider({ children, mode = 'auto' }) {
  const { socket: authSocket } = useSocket() || {};
  const [guestSocket, setGuestSocket] = useState(null);
  const guestRef = useRef(null);
  const [events, setEvents] = useState([]);
  const queueRef = useRef([]);
  const flushRef = useRef(null);
  const keysRef = useRef(new Set());

  useEffect(() => {
    if (mode !== 'public') {
      if (guestRef.current) {
        guestRef.current.removeAllListeners();
        guestRef.current.disconnect();
        guestRef.current = null;
        setGuestSocket(null);
      }
      return;
    }
    if (guestRef.current) return;
    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
      query: { guest: 'true' },
    });
    guestRef.current = socket;
    setGuestSocket(socket);
    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      if (guestRef.current === socket) {
        guestRef.current = null;
        setGuestSocket(null);
      }
    };
  }, [mode]);

  const socket = mode === 'public' ? guestSocket : authSocket;

  const flushQueue = useCallback(() => {
    flushRef.current = null;
    const queued = queueRef.current.splice(0);
    if (!queued.length) return;
    setEvents((prev) => {
      const next = [];
      const keys = new Set(keysRef.current);
      queued.forEach((event) => {
        if (!event || !event.id) return;
        if (keys.has(event.id)) return;
        keys.add(event.id);
        next.push(event);
      });
      if (!next.length) return prev;
      const merged = [...next, ...prev].sort(
        (a, b) => (b.createdAt || 0) - (a.createdAt || 0),
      );
      const trimmed = merged.slice(0, MAX_LIVE_EVENTS);
      keysRef.current = new Set(trimmed.map((event) => event.id));
      return trimmed;
    });
  }, []);

  const enqueueEvent = useCallback(
    (event) => {
      if (!event || !event.id) return;
      queueRef.current.push(event);
      if (flushRef.current) return;
      flushRef.current = setTimeout(flushQueue, LOCALOPS_LIVE_BATCH_MS);
    },
    [flushQueue],
  );

  const handleLive = useCallback(
    (payload) => {
      if (mode === 'public' && normalizeVisibility(payload?.visibility) !== 'public') {
        return;
      }
      if (mode === 'auth' && normalizeVisibility(payload?.visibility) !== 'auth') {
        return;
      }
      const event = normalizeLivePayload(payload);
      if (!event) return;
      enqueueEvent(event);
    },
    [enqueueEvent, mode],
  );

  useEffect(() => {
    if (!socket) return;
    const handler = (payload) => handleLive(payload);
    socket.on('localops:live', handler);
    socket.on('localops.live.event', handler);
    return () => {
      socket.off('localops:live', handler);
      socket.off('localops.live.event', handler);
    };
  }, [socket, handleLive]);

  useEffect(() => {
    return () => {
      if (flushRef.current) {
        clearTimeout(flushRef.current);
      }
    };
  }, []);

  const value = useMemo(() => ({ events }), [events]);

  return (
    <LocalOpsLiveContext.Provider value={value}>
      {children}
    </LocalOpsLiveContext.Provider>
  );
}

export const useLocalOpsLive = () => useContext(LocalOpsLiveContext);
